import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import API_BASE_URL from '../../config';
import { saveJson } from '../../utils/apiRequest';
import { istEntschieden } from '../../utils/freigabeStand';
import SeitenTitel from '../../components/ui/SeitenTitel';
import Projektfreigaben from '../../components/kunde/Projektfreigaben';
import Freigabefrist from '../../components/kunde/Freigabefrist';

export default function Freigaben() {
  const { token, user } = useAuth();
  const [project, setProject] = useState(null);
  const [freigaben, setFreigaben] = useState({});
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState({});
  const [comments, setComments] = useState({});
  const [ladefehler, setLadefehler] = useState(false);

  useEffect(() => {
    loadProject();
  }, [token]); // eslint-disable-line

  // Schlug hier etwas fehl, zeigte die Seite „Keine offenen Freigaben
  // vorhanden" — also Entwarnung, obwohl gar nicht geladen werden konnte.
  // Ein Ladefehler muss als Ladefehler dastehen.
  const loadProject = async () => {
    setLadefehler(false);
    try {
      // Finde das eigene Projekt über portal/me
      const res = await fetch(`${API_BASE_URL}/api/portal/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { setLadefehler(true); setLoading(false); return; }
      const portalData = await res.json();
      if (!portalData.project_id) { setLoading(false); return; }

      // Lade Projekt-Details für content_freigaben
      const pRes = await fetch(`${API_BASE_URL}/api/projects/${portalData.project_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!pRes.ok) { setLadefehler(true); setLoading(false); return; }

      const p = await pRes.json();
      setProject(p);
      try {
        const cf = typeof p.content_freigaben === 'string' ? JSON.parse(p.content_freigaben) : (p.content_freigaben || {});
        setFreigaben(cf);
      } catch { setFreigaben({}); }
    } catch (e) {
      setLadefehler(true);
    }
    setLoading(false);
  };

  // Vorher stand hier ein `catch (e) { console.error(e) }` um ein `fetch`,
  // das `res.ok` nicht prüfte. Der Endpunkt verlangte Adminrechte — der Kunde
  // bekam 403, sah aber nichts davon: Die Seite lud neu und zeigte weiter
  // „ausstehend". Ein Fehler, den niemand sieht, ist ein Fehler, der bleibt
  // (Lücke L-36). `saveJson` meldet ihn und liefert true/false.
  const handleDecision = async (seiteId, confirmed) => {
    if (!project) return;
    setSending(s => ({ ...s, [seiteId]: true }));

    const gespeichert = await saveJson(
      `${API_BASE_URL}/api/projects/${project.id}/confirm-approval`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          seite_id: seiteId,
          bestaetigt: confirmed,
          kommentar: comments[seiteId] || '',
        }),
      },
      { context: confirmed ? 'Freigabe' : 'Ablehnung' },
    );

    if (gespeichert) await loadProject();
    setSending(s => ({ ...s, [seiteId]: false }));
  };

  const cardStyle = {
    background: 'var(--bg-surface)', borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--border-light)', padding: '20px 22px', marginBottom: 14,
  };

  // Als entschieden gilt nur, was wirklich entschieden wurde. Der Filter
  // stand auf „ausstehend"; das Backend schreibt beim Anfragen aber
  // „angefragt" — eine offene Anfrage landete dadurch unter „erledigt" und
  // verschwand aus genau der Liste, die der Kunde abarbeiten soll. Statt
  // einer Datenmigration prüfen wir auf die beiden Endzustände, damit
  // Altdatensätze beider Schreibweisen richtig bleiben.
  const items = Object.entries(freigaben);
  const pending = items.filter(([, v]) => !istEntschieden(v));
  const decided = items.filter(([, v]) => istEntschieden(v));

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '0 0 40px' }}>
      <SeitenTitel>Freigaben</SeitenTitel>
      <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 20 }}>
        ✅ Freigaben
      </div>

      {/* **Die Frist steht über der Liste** (L-160, Rang 3). „Sie haben fünf
          Werktage" stand im Angebot und nirgends auf dem Bildschirm, auf dem
          der Kunde tatsächlich freigibt — er entschied, ohne zu wissen, dass
          an seiner Entscheidung ein Termin hängt. */}
      <Freigabefrist token={token} />

      {loading && <div style={{ color: 'var(--text-tertiary)', fontSize: 13 }}>Wird geladen…</div>}

      {!loading && ladefehler && (
        <div style={{ ...cardStyle, textAlign: 'center', fontSize: 13, padding: 40, color: 'var(--status-danger-text)' }}>
          Ihre Freigaben konnten nicht geladen werden.
          <div style={{ fontSize: 12, marginTop: 8, color: 'var(--text-tertiary)' }}>
            Bitte laden Sie die Seite neu. Bleibt es dabei, melden Sie sich bei uns —
            es liegt nicht an Ihnen.
          </div>
          <button
            onClick={() => { setLoading(true); loadProject(); }}
            style={{ marginTop: 14, padding: '8px 16px', border: '1px solid var(--border-medium)', borderRadius: 'var(--radius-md)', background: 'var(--bg-surface)', color: 'var(--text-primary)', fontSize: 12, cursor: 'pointer' }}
          >
            Erneut versuchen
          </button>
        </div>
      )}

      {!loading && !ladefehler && items.length === 0 && (
        <div style={{ ...cardStyle, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13, padding: 40 }}>
          Keine offenen Freigaben vorhanden.
          <div style={{ fontSize: 12, marginTop: 8, color: 'var(--text-tertiary)' }}>
            Ihr KOMPAGNON-Team sendet Ihnen Freigabeanfragen, sobald Inhalte bereit sind.
          </div>
        </div>
      )}

      {/* Offene Freigaben */}
      {pending.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-tertiary)', letterSpacing: '0.06em', marginBottom: 10 }}>
            OFFEN ({pending.length})
          </div>
          {pending.map(([seiteId, item]) => (
            <div key={seiteId} style={{ ...cardStyle, borderLeft: '3px solid var(--brand-primary)' }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>
                {item.topic || item.label || `Seite ${seiteId}`}
              </div>
              {item.beschreibung && (
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10, lineHeight: 1.5 }}>
                  {item.beschreibung}
                </div>
              )}
              {item.preview_url && (
                <a href={item.preview_url} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'inline-block', fontSize: 12, color: 'var(--brand-primary-mid)', marginBottom: 10 }}>
                  Vorschau öffnen →
                </a>
              )}
              <textarea aria-label="Kommentar (optional)…"
                placeholder="Kommentar (optional)…"
                value={comments[seiteId] || ''}
                onChange={e => setComments(c => ({ ...c, [seiteId]: e.target.value }))}
                rows={2}
                style={{
                  width: '100%', boxSizing: 'border-box', padding: '8px 12px', borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-light)', background: 'var(--bg-app)',
                  color: 'var(--text-primary)', fontSize: 12, fontFamily: 'var(--font-sans)', resize: 'vertical', outline: 'none',
                  marginBottom: 10,
                }}
              />
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => handleDecision(seiteId, true)} disabled={sending[seiteId]}
                  style={{
                    // Gruen war hier die einzige Stelle der Anwendung, die eine
                    // Hauptaktion nicht in der Markenfarbe zeigt. Der Unterschied
                    // zu „Ablehnen" steht im Wort, nicht allein in der Farbe.
                    padding: '8px 18px', background: 'var(--brand-primary)',
                    color: 'var(--text-on-brand)', border: 'none',
                    borderRadius: 'var(--radius-md)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    fontFamily: 'var(--font-sans)', opacity: sending[seiteId] ? 0.6 : 1,
                  }}>Freigeben</button>
                <button onClick={() => handleDecision(seiteId, false)} disabled={sending[seiteId]}
                  style={{
                    padding: '8px 18px', background: 'var(--status-danger-bg)', color: 'var(--status-danger-text)',
                    border: '1px solid var(--status-danger-text)', borderRadius: 'var(--radius-md)',
                    fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-sans)',
                    opacity: sending[seiteId] ? 0.6 : 1,
                  }}>Ablehnen</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Erledigte Freigaben */}
      {decided.length > 0 && (
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-tertiary)', letterSpacing: '0.06em', marginBottom: 10 }}>
            ERLEDIGT ({decided.length})
          </div>
          {decided.map(([seiteId, item]) => (
            <div key={seiteId} style={{ ...cardStyle, opacity: 0.7 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
                  {item.topic || item.label || `Seite ${seiteId}`}
                </div>
                <span style={{
                  padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600,
                  background: item.status === 'freigegeben' ? 'var(--status-success-bg)' : 'var(--status-danger-bg)',
                  color: item.status === 'freigegeben' ? 'var(--status-success-text)' : 'var(--status-danger-text)',
                }}>
                  {item.status === 'freigegeben' ? 'Freigegeben' : 'Abgelehnt'}
                </span>
              </div>
              {item.freigegeben_am && (
                <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 4 }}>
                  am {item.freigegeben_am}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {/* Die elf Freigaben zum Projektablauf (26.08.2026, L-105). Sie
        * standen bisher nur im Innendienst, wo sie **wir** abgehakt haben —
        * eine Abnahme, die der Auftragnehmer selbst abhakt, ist keine.
        * Unter den Inhaltsfreigaben, weil die das taegliche Geschaeft sind
        * und diese hier den Ablauf begleiten. */}
      <Projektfreigaben leadId={user?.lead_id} token={token} />

    </div>
  );
}
